import { saveAs } from "file-saver"; // optional helper library
import { useState } from "react";
import { pdf } from "@react-pdf/renderer";

import {
  Box,
  Grid2 as Grid,
  Typography,
  Divider,
  TextField,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  CardContent,
  Card,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { Download } from "@phosphor-icons/react";
import { Eye } from "@phosphor-icons/react";

import TopNavBar from "../../components/TopNavBar";
import CertificatePreview from "./CertificatePreview";
import { CertificateFormData } from "./types";
import BaptismCertificatePDF from "./BaptismCertificatePDF";
import { updateAnalytics } from "../../utils/client";

const styles = {
  mainBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  actionButton: {
    maxWidth: "325px",
    height: 60,
    backgroundColor: "#00169E",
    color: "#FFF",
    fontSize: 16,
  },
};

const CertificateGenerator = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<CertificateFormData>({
    name: "",
    baptismDate: "June 8, 2025",
    designChoice: "corinthians",
  });

  const [errors, setErrors] = useState<{
    name?: string;
  }>({});

  const handleChange =
    (field: keyof CertificateFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };
  const handleSelectChange =
    (field: keyof CertificateFormData) =>
    (event: SelectChangeEvent<string>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };
  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setFormData((prev) => ({
      ...prev,
      designChoice: newValue as CertificateFormData["designChoice"],
    }));
  };

  const validateInputs = () => {
    const newErrors: typeof errors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Your name is required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleDownload = async () => {
    if (!validateInputs()) return;
    // console.log("Download certificate for:", formData.firstName, formData.middleName, formData.lastName);
    try {
      setLoading(true);
      const blob = await pdf(
        <BaptismCertificatePDF data={formData} />,
      ).toBlob();
      saveAs(blob, "baptism-certificate.pdf");
      await updateAnalytics({ certificate: true });
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  return (
    <div>
      <Box sx={styles.mainBox}>
        <TopNavBar showBackArrow={false} logoType={"america"} />
      </Box>
      <Box p={4}>
        <Grid container spacing={4} sx={{ justifyContent: "center" }}>
          {/* Form Section */}
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  Generate Your Baptism Certificate
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2} sx={{ mt: 1 }} direction={"row"}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      Personal Information
                    </Typography>
                    <TextField
                      label="Full Name"
                      fullWidth
                      required
                      value={formData.name}
                      onChange={handleChange("name")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      Baptism Information
                    </Typography>
                    <Select
                      label={null}
                      onChange={handleSelectChange("baptismDate")}
                      value={formData.baptismDate}
                      sx={{ mb: 3 }}
                      fullWidth
                    >
                      <MenuItem key={"Select_Saturday"} value={"June 7, 2025"}>
                        June 7, 2025 (Saturday)
                      </MenuItem>
                      <MenuItem key={"Select_Sunday"} value={"June 8, 2025"}>
                        June 8, 2025 (Sunday)
                      </MenuItem>
                    </Select>
                  </Grid>
                </Grid>
                <Grid display={"flex"} justifyContent={"center"}>
                  <Button
                    onClick={handleDownload}
                    variant="contained"
                    fullWidth
                    sx={styles.actionButton}
                    startIcon={
                      loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <Download size={24} />
                      )
                    }
                  >
                    {loading ? "" : "Download Certificate"}
                  </Button>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Preview Section */}
          <Grid size={12} sx={{ justifyContent: "center" }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Eye size={24} style={{ marginRight: 8 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Certificate Preview
                  </Typography>
                </Box>
                <Tabs
                  value={formData.designChoice}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{ mb: 5 }}
                >
                  <Tab value="corinthians" label="Corinthians" />
                  <Tab value="colossians" label="Colossians" />
                  <Tab value="romans" label="Romans" />
                </Tabs>
                <Card sx={{ justifyContent: "center" }}>
                  <CardContent>
                    <CertificatePreview data={formData} />
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default CertificateGenerator;
